import * as pc from 'playcanvas';
import { smoothDamp, smoothDampVec3, smoothDampQuat, approxZero, PI, DEG2RAD, clamp, setQuatFromUnitVectors } from '../utils/math-utils';
import { EventDispatcher } from './event-dispatcher';
import { extractClientCoordFromEvent } from './extract-client-coord-from-event';
import { 
    Ref,
    MOUSE_BUTTON, 
    ACTION, 
    DOLLY_DIRECTION, 
    PointerInput,
    MouseButtons,
    Touches,
    FitToOptions, 
    CameraControlsEventMap,
    isPerspectiveCamera, 
    isOrthographicCamera, 
    CameraControlsLerpState } from '../types';

/**
 * A universal camera controller for PlayCanvas that can be driven by:
 * 1. Native PlayCanvas Input (Mouse/Touch) via .connect()
 * 2. External Input Frames (e.g. Supersplat Viewer) via .rotate()/.truck()/.dolly()
 * 3. Custom Editor Events (e.g. Supersplat Editor)
 * 
 * Ported from yomotsu/camera-controls to use native PlayCanvas math for maximum performance.
 */
export class CameraControls extends EventDispatcher {
    public readonly entity: pc.Entity;
    
    // --- State ---
    private _target = new pc.Vec3();
    private _targetEnd = new pc.Vec3();
    private _position = new pc.Vec3();
    private _spherical = new pc.Vec3(); // Radius, Phi, Theta
    private _sphericalEnd = new pc.Vec3(); // Radius, Phi, Theta
    private _focalOffset = new pc.Vec3();
    private _focalOffsetEnd = new pc.Vec3();
    private _zoom = 1;
    private _zoomEnd = 1;
    private _enabled = true;
    
    public readonly rotation = new pc.Quat();

    // --- Reset State ---
    private _target0 = new pc.Vec3();
    private _position0 = new pc.Vec3();
    private _zoom0 = 1;
    private _focalOffset0 = new pc.Vec3();
    
    // --- Configuration ---
    /**
     * Minimum distance for dolly. Default is `Number.EPSILON`.
     * PerspectiveCamera only.
     * @category Properties
     */
    public minDistance = 1e-5;
    /**
     * Maximum distance for dolly. The value must be higher than `minDistance`.
     * Default is `Infinity`.
     * PerspectiveCamera only.
     * @category Properties
     */
    public maxDistance = Infinity;
    /**
     * Minimum vertical angle in radians.
     * The angle has to be between `0` and `.maxPolarAngle` inclusive.
     * The default value is `0`.
     *
     * e.g.
     * ```
     * cameraControls.maxPolarAngle = 0;
     * ```
     * @category Properties
     */
    public minPolarAngle = 0; // radians
    /**
     * Maximum vertical angle in radians.
     * The angle has to be between `.maxPolarAngle` and `Math.PI` inclusive.
     * The default value is `Math.PI`.
     *
     * e.g.
     * ```
     * cameraControls.maxPolarAngle = Math.PI;
     * ```
     * @category Properties
     */
    public maxPolarAngle = Math.PI; // radians
    /**
     * Minimum horizontal angle in radians.
     * The angle has to be less than `.maxAzimuthAngle`.
     * The default value is `- Infinity`.
     *
     * e.g.
     * ```
     * cameraControls.minAzimuthAngle = - Infinity;
     * ```
     * @category Properties
     */
    public minAzimuthAngle = -Infinity; // radians
    /**
     * Maximum horizontal angle in radians.
     * The angle has to be greater than `.minAzimuthAngle`.
     * The default value is `Infinity`.
     *
     * e.g.
     * ```
     * cameraControls.maxAzimuthAngle = Infinity;
     * ```
     * @category Properties
     */
    public maxAzimuthAngle = Infinity; // radians
    /**
     * @deprecated use smoothTime (in seconds) instead
     */
    public dampingFactor = 0.2; // Higher = faster
    /**
     * @deprecated use draggingSmoothTime (in seconds) instead
     */
    public draggingDampingFactor = 0.25;
    public enableDamping = true;
    /**
     * Approximate time in seconds to reach the target. A smaller value will reach the target faster.
     * @category Properties
     */
    public smoothTime = 0.25;
    /**
     * the smoothTime while dragging
     * @category Properties
     */
    public draggingSmoothTime = 0.1;
    /**
     * Max transition speed in unit-per-seconds
     * @category Properties
     */
    public maxSpeed = Infinity;
    public restThreshold = 0.01;
    
    public autoRotate = false;
    public autoRotateSpeed = 2.0 * DEG2RAD; // radians per second

    /**
     * Speed of azimuth (horizontal) rotation.
     * @category Properties
     */
    public azimuthRotateSpeed = 1.0;
    /**
     * Speed of polar (vertical) rotation.
     * @category Properties
     */
    public polarRotateSpeed = 1.0;
    /**
     * Speed of mouse-wheel dollying.
     * @category Properties
     */
    public dollySpeed = 1.0;
    /**
     * Speed of drag for truck and pedestal.
     * @category Properties
     */
    public truckSpeed = 2.0;
    /**
     * `true` to invert direction when dollying or zooming via drag
     * @category Properties
     */
    public dollyDragInverted = false;
    public dollyToCursor = false;
    public dragToOffset = false;
    public boundaryFriction = 0.0;
    /**
     * `true` to enable Infinity Dolly for wheel and pinch. Use this with `minDistance` and `maxDistance`
     * If the Dolly distance is less (or over) than the `minDistance` (or `maxDistance`), `infinityDolly` will keep the distance and pushes the target position instead.
     * @category Properties
     */
    public infinityDolly = false;
    /**
     * Minimum camera zoom.
     * @category Properties
     */
    public minZoom = 0.01;
    /**
     * Maximum camera zoom.
     * @category Properties
     */
    public maxZoom = Infinity;

    /**
     * If true, the camera will always look at the target, even while moving.
     * This eliminates the "spring" effect during orbiting but may cause the camera to rotate unwantedly during trucking.
     * @category Properties
     */
    public alignRotationToTarget = true;

    /**
	 * Whether or not the controls are enabled.  
	 * `false` to disable user dragging/touch-move, but all methods works.
	 * @category Properties
	 */
	get enabled(): boolean {

		return this._enabled;

	}

	set enabled( enabled: boolean ) {

		this._enabled = enabled;

		if ( ! this._domElement ) return;
		if ( enabled ) {

			this._domElement.style.touchAction = 'none';
			this._domElement.style.userSelect = 'none';
			this._domElement.style.webkitUserSelect = 'none';

		} else {

			this.cancel();
			this._domElement.style.touchAction = '';
			this._domElement.style.userSelect = '';
			this._domElement.style.webkitUserSelect = '';

		}

	}

    public get active(): boolean {
        return !this._hasRested;
    }

    public get currentAction(): ACTION {
        return this._state;
    }

    public get boundaryEnclosesCamera(): boolean {
        return this._boundaryEnclosesCamera;
    }

    public set boundaryEnclosesCamera(value: boolean) {
        this._boundaryEnclosesCamera = value;
    }

    public set interactiveArea(area: DOMRect | { x: number, y: number, width: number, height: number }) {
        this._interactiveArea.x = pc.math.clamp(area.x, 0, 1);
        this._interactiveArea.y = pc.math.clamp(area.y, 0, 1);
        this._interactiveArea.width = pc.math.clamp(area.width, 0, 1);
        this._interactiveArea.height = pc.math.clamp(area.height, 0, 1);
    }

    public set azimuthAngle(angle: number) {
        this._spherical.z = angle;
        this._sphericalEnd.z = angle;
    }

    public set polarAngle(angle: number) {
        this._spherical.y = angle;
        this._sphericalEnd.y = angle;
    }

    public mouseButtons: MouseButtons = {
        left: ACTION.ROTATE,
        middle: ACTION.DOLLY,
        right: ACTION.TRUCK,
        wheel: ACTION.DOLLY
    };

    public touches: Touches = {
        one: ACTION.TOUCH_ROTATE,
        two: ACTION.TOUCH_DOLLY_TRUCK,
        three: ACTION.TOUCH_TRUCK
    };

    // --- Input Helpers ---
    private _domElement: HTMLElement | null = null;
    public colliderMeshes: pc.MeshInstance[] = [];
    private _activePointers: PointerInput[] = [];
    private _elementRect: DOMRect | null = null;
    private _viewport: pc.Vec4 | null = null;

    // --- Internals (Temp variables to avoid GC) ---
    private _tempVec = new pc.Vec3();
    private _tempVec2 = new pc.Vec3();
    private _tempVec3 = new pc.Vec3();
    private _fromWorldPoint = new pc.Vec3();
    private _toWorldPoint = new pc.Vec3();
    private _worldDiff = new pc.Vec3();
    private _clientCoord = new pc.Vec2();
    private _dragStartPosition = new pc.Vec2();
    private _lastDragPosition = new pc.Vec2();
    private _dollyStart = new pc.Vec2();
    private _dollyControlCoord = new pc.Vec2();
    private _tempMat = new pc.Mat4();

    // --- Velocities for smoothDamp ---
    private _cameraPosVelocity = new pc.Vec3();
    private _cameraRotVelocity = new pc.Quat(); // Stores rate of change for x,y,z,w
    private _cameraZoomVelocity: Ref = { value: 0 };
    
    private _desiredPos = new pc.Vec3();
    private _desiredRot = new pc.Quat();

    private _hasRested = true;
    
    // --- Robust Commit Gate State ---
    private _isSleeping = true;
    
    private _state: ACTION = ACTION.NONE;
    private _isUserControllingRotate = false;
    private _isUserControllingDolly = false;
    private _isUserControllingTruck = false;
    private _isUserControllingZoom = false;
    
    private _lastDollyDirection: DOLLY_DIRECTION = DOLLY_DIRECTION.NONE;
    private _changedDolly = 0;
    private _changedZoom = 0;
    
    private _lastSpherical = new pc.Vec3();
    private _lastTarget = new pc.Vec3();
    private _lastFocalOffset = new pc.Vec3();
    private _lastRotation = new pc.Quat();
    
    // --- Committed State (Shadow Authoritative State) ---
    private _committedPos = new pc.Vec3();
    private _committedRot = new pc.Quat();
    private _committedZoom = 1;
    
    private _boundary = new pc.BoundingBox();
    private _boundaryEnclosesCamera = false;
    private _interactiveArea = new DOMRect(0, 0, 1, 1);
    
    private _cameraUp0 = new pc.Vec3();
    private _yAxisUpSpace = new pc.Quat();
    private _yAxisUpSpaceInverse = new pc.Quat();
    private _nearPlaneCorners: [pc.Vec3, pc.Vec3, pc.Vec3, pc.Vec3] = [
        new pc.Vec3(), new pc.Vec3(), new pc.Vec3(), new pc.Vec3()
    ];

    constructor(entity: pc.Entity) {
        super();
        this.entity = entity;
        this._position.copy(entity.getPosition());
        
        // Initialize target at some distance forward if not set
        const forward = entity.forward;
        this._target.copy(this._position).add(forward.mulScalar(10)); // Default target 10 units away
        this._targetEnd.copy(this._target);
        
        this._updateSpherical();
        this._sphericalEnd.copy(this._spherical);

        this._lastSpherical.copy(this._spherical);
        this._lastTarget.copy(this._target);
        this._lastFocalOffset.copy(this._focalOffset);

        // Initialize rotation state
        this._tempMat.setLookAt(this._position, this._target, pc.Vec3.UP);
        this.rotation.setFromMat4(this._tempMat);
        this._lastRotation.copy(this.rotation);

        this._committedPos.copy(this._position);
        this._committedRot.copy(this.rotation);
        this._committedZoom = this._zoom;

        this._cameraUp0.copy(entity.up);
        this.updateCameraUp();

        this._target0.copy(this._target);
        this._position0.copy(this._position);
        this._zoom0 = this._zoom;
        this._focalOffset0.copy(this._focalOffset);

        this._boundary.center.set(0, 0, 0);
        this._boundary.halfExtents.set(Infinity, Infinity, Infinity);
        
        this._updateNearPlaneCorners();
    }

    public override addEventListener<K extends keyof CameraControlsEventMap>( type: K, listener: ( event: CameraControlsEventMap[ K ] ) => void ): void {
		super.addEventListener( type, listener as any );
	}

	public override hasEventListener<K extends keyof CameraControlsEventMap>( type: K, listener: ( event: CameraControlsEventMap[ K ] ) => void ): boolean {
		return super.hasEventListener( type, listener as any );
	}

	public override removeEventListener<K extends keyof CameraControlsEventMap>( type: K, listener: ( event: CameraControlsEventMap[ K ] ) => void ): void {
		super.removeEventListener( type, listener as any );
	}

	public override removeAllEventListeners<K extends keyof CameraControlsEventMap>( type?: K ): void {
		super.removeAllEventListeners( type );
	}

    /**
     * Connects native PlayCanvas input listeners to a specific element.
     */
    public connect(element: HTMLElement) {
        if (this._domElement) return;
        this._domElement = element;
        // Prevent default touch actions (scrolling/zooming)
        element.style.touchAction = 'none';
        element.style.userSelect = 'none';
        element.style.webkitUserSelect = 'none';
        
        element.addEventListener('pointerdown', this._onPointerDown);
        element.addEventListener('wheel', this._onWheel, { passive: false });
        element.addEventListener('contextmenu', this._onContextMenu);

        this._elementRect = element.getBoundingClientRect();
    }

    public cancel() {
        if (this._domElement) {
            for (const p of this._activePointers) {
                try {
                    this._domElement.releasePointerCapture(p.pointerId);
                } catch (e) {
                    // ignore
                }
            }
            this._domElement.removeEventListener('pointermove', this._onPointerMove);
            this._domElement.removeEventListener('pointerup', this._onPointerUp);
        }

        this._activePointers = [];
        this._state = ACTION.NONE;
        this._isUserControllingRotate = false;
        this._isUserControllingTruck = false;
        this._isUserControllingDolly = false;
        this._isUserControllingZoom = false;
        
        this.dispatchEvent({ type: 'controlend' });
    }

    public disconnect() {
        if (!this._domElement) return;

        this.cancel();

        this._domElement.style.touchAction = '';
        this._domElement.style.userSelect = '';
        this._domElement.style.webkitUserSelect = '';

        this._domElement.removeEventListener('pointerdown', this._onPointerDown);
        this._domElement.removeEventListener('wheel', this._onWheel);
        this._domElement.removeEventListener('contextmenu', this._onContextMenu);
        
        const doc = this._domElement.ownerDocument;
        doc.removeEventListener('pointermove', this._onPointerMove);
        doc.removeEventListener('pointerup', this._onPointerUp);
        
        this._domElement = null;
        this._activePointers = [];
        this._elementRect = null;
    }

    /**
     * Rotate the camera around the target (Orbit)
     * @param azimuthDelta Change in azimuth (horizontal) in radians
     * @param polarDelta Change in polar (vertical) in radians
     * @param enableTransition Whether to move smoothly
     */
    public rotate(azimuthDelta: number, polarDelta: number, enableTransition = false): Promise<void> {
        return this.rotateTo(
            this._sphericalEnd.z + azimuthDelta,
            this._sphericalEnd.y + polarDelta,
            enableTransition
        );
    }

    public rotateTo(azimuthAngle: number, polarAngle: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._rotateToInternal(azimuthAngle, polarAngle, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _rotateToInternal(azimuthAngle: number, polarAngle: number, enableTransition = false): boolean {
        this._isUserControllingRotate = false;
        
        const theta = pc.math.clamp(azimuthAngle, this.minAzimuthAngle, this.maxAzimuthAngle);
        const phi = pc.math.clamp(polarAngle, this.minPolarAngle, this.maxPolarAngle);

        this._sphericalEnd.z = theta;
        this._sphericalEnd.y = phi;
        
        if (!enableTransition) {
            this._spherical.y = this._sphericalEnd.y;
            this._spherical.z = this._sphericalEnd.z;
        }

        return !enableTransition ||
            approxZero(this._spherical.z - this._sphericalEnd.z, this.restThreshold) &&
            approxZero(this._spherical.y - this._sphericalEnd.y, this.restThreshold);
    }

    public rotateAzimuthTo(azimuthAngle: number, enableTransition = false): Promise<void> {
        return this.rotateTo(azimuthAngle, this._sphericalEnd.y, enableTransition);
    }

    public rotatePolarTo(polarAngle: number, enableTransition = false): Promise<void> {
        return this.rotateTo(this._sphericalEnd.z, polarAngle, enableTransition);
    }

    /**
     * Pan the camera and target (Truck)
     * @param x Horizontal movement
     * @param y Vertical movement
     * @param enableTransition Whether to move smoothly
     */
    public truck(x: number, y: number, enableTransition = false): Promise<void> {
        // Use camera's local axes for screen-space panning
        const right = this.entity.right;
        const up = this.entity.up;

        const moveX = this._tempVec.copy(right).mulScalar(-x);
        const moveY = this._tempVec2.copy(up).mulScalar(-y);

        const offset = moveX.add(moveY);
        const to = this._worldDiff.copy(this._targetEnd).add(offset);
        
        return this.moveTo(to.x, to.y, to.z, enableTransition);
    }

    /**
     * Move forward / backward.
     * @param distance Amount to move forward / backward. Negative value to move backward
     * @param enableTransition Whether to move smoothly
     */
    public forward(distance: number, enableTransition = false): Promise<void> {
        const forward = this._tempVec.sub2(this._target, this._position).normalize();
        forward.mulScalar(distance);
        const to = this._tempVec2.copy(this._targetEnd).add(forward);
        
        return this.moveTo(to.x, to.y, to.z, enableTransition);
    }

    /**
     * Move up / down.
     * @param height Amount to move up / down. Negative value to move down
     * @param enableTransition Whether to move smoothly
     */
    public elevate(height: number, enableTransition = false): Promise<void> {
        const up = this._tempVec.copy(this.entity.up).mulScalar(height);
        return this.moveTo(
            this._targetEnd.x + up.x,
            this._targetEnd.y + up.y,
            this._targetEnd.z + up.z,
            enableTransition
        );
    }

    public moveTo(x: number, y: number, z: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._moveToInternal(x, y, z, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _moveToInternal(x: number, y: number, z: number, enableTransition = false): boolean {
        this._isUserControllingTruck = false;

        const offset = this._fromWorldPoint.set(x, y, z).sub(this._targetEnd);
        this._encloseToBoundary(this._targetEnd, offset, this.boundaryFriction);
        
        if (!enableTransition) {
            this._target.copy(this._targetEnd);
        }

        return !enableTransition ||
            approxZero(this._target.x - this._targetEnd.x, this.restThreshold) &&
            approxZero(this._target.y - this._targetEnd.y, this.restThreshold) &&
            approxZero(this._target.z - this._targetEnd.z, this.restThreshold);
    }

    /**
     * Pan the camera based on screen-space movement.
     * This ensures that the point under the cursor stays under the cursor.
     * @param x Screen X
     * @param y Screen Y
     * @param dx Screen delta X
     * @param dy Screen delta Y
     */
    public truckScreen(x: number, y: number, dx: number, dy: number) {
        if (!this.entity.camera) return;

        const distance = this.distance;
        
        this.entity.camera.screenToWorld(x, y, distance, this._fromWorldPoint);
        this.entity.camera.screenToWorld(x - dx, y - dy, distance, this._toWorldPoint);

        this._worldDiff.sub2(this._toWorldPoint, this._fromWorldPoint);
        
        this._targetEnd.add(this._worldDiff);
        
        this.dispatchEvent({ type: 'control' });
    }

    /**
     * Move camera along the forward vector (Dolly)
     * @param distance Distance to move
     * @param enableTransition Whether to move smoothly
     */
    public dolly(distance: number, enableTransition = false): Promise<void> {
        return this.dollyTo(this._sphericalEnd.x - distance, enableTransition);
    }

    /**
     * Dolly to a specific distance
     * @param distance Target distance
     * @param enableTransition Whether to move smoothly
     */
    public dollyTo(distance: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._dollyToInternal(distance, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _dollyToInternal(distance: number, enableTransition = false): boolean {
        this._isUserControllingDolly = false;
        this._lastDollyDirection = DOLLY_DIRECTION.NONE;
        this._changedDolly = 0;
        
        const newDist = pc.math.clamp(distance, this.minDistance, this.maxDistance);
        this._sphericalEnd.x = newDist;
        
        if (!enableTransition) {
            this._spherical.x = newDist;
        }

        return !enableTransition ||
            approxZero(this._spherical.x - this._sphericalEnd.x, this.restThreshold);
    }

    public dollyInFixed(distance: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._dollyInFixedInternal(distance, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _dollyInFixedInternal(distance: number, enableTransition = false): boolean {
        const cameraDirection = this._tempVec.sub2(this._target, this._position).normalize();
        this._targetEnd.add(cameraDirection.mulScalar(distance));

        if (!enableTransition) {
            this._target.copy(this._targetEnd);
        }
        
        return !enableTransition ||
            approxZero(this._target.x - this._targetEnd.x, this.restThreshold) &&
            approxZero(this._target.y - this._targetEnd.y, this.restThreshold) &&
            approxZero(this._target.z - this._targetEnd.z, this.restThreshold);
    }

    /**
     * Zoom the camera
     * @param zoomStep Zoom step
     * @param enableTransition Whether to move smoothly
     */
    public zoom(zoomStep: number, enableTransition = false): Promise<void> {
        return this.zoomTo(this._zoomEnd + zoomStep, enableTransition);
    }

    /**
     * Zoom to a specific value
     * @param zoom Target zoom
     * @param enableTransition Whether to move smoothly
     */
    public zoomTo(zoom: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._zoomToInternal(zoom, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _zoomToInternal(zoom: number, enableTransition = false): boolean {
        this._isUserControllingZoom = false;
        this._zoomEnd = pc.math.clamp(zoom, this.minZoom, this.maxZoom);
        
        if (!enableTransition) {
            this._zoom = this._zoomEnd;
        }

        this._changedZoom = 0;
        return !enableTransition ||
            approxZero(this._zoom - this._zoomEnd, this.restThreshold);
    }

    /**
     * Set the absolute orbit angles
     * @param azimuth Azimuth angle in radians
     * @param polar Polar angle in radians
     * @param enableTransition Whether to move smoothly
     */
    public setOrbit(azimuth: number, polar: number, enableTransition = false): Promise<void> {
        return this.rotateTo(azimuth, polar, enableTransition);
    }

    /**
     * Set the distance from the target
     * @param distance Distance
     * @param enableTransition Whether to move smoothly
     */
    public setDistance(distance: number, enableTransition = false): Promise<void> {
        return this.dollyTo(distance, enableTransition);
    }

    /**
     * Set the target position (Focal Point)
     * @param x 
     * @param y 
     * @param z 
     * @param enableTransition
     */
    public setTarget(x: number, y: number, z: number, enableTransition = false): Promise<void> {
        const promise = this.setLookAt(
            this._position.x, this._position.y, this._position.z,
            x, y, z,
            enableTransition
        );

        this._sphericalEnd.y = pc.math.clamp(this._sphericalEnd.y, this.minPolarAngle, this.maxPolarAngle);

        if (!enableTransition) {
            this._spherical.y = this._sphericalEnd.y;
            this._updatePositionFromSpherical();
        }
        
        this.dispatchEvent({ type: 'control' });
        return promise;
    }

    /**
     * Snap the camera to the current target values immediately, bypassing damping.
     */
    public snap() {
        this._spherical.copy(this._sphericalEnd);
        this._target.copy(this._targetEnd);
        this._updatePositionFromSpherical();
        this.entity.setPosition(this._position);
        this.entity.lookAt(this._target);
    }

    /**
     * Calculate the distance to fit the box.
     * @param width box width
     * @param height box height
     * @param depth box depth
     * @returns distance
     */
    public getDistanceToFitBox(width: number, height: number, depth: number, cover = false): number {
        if (!this.entity.camera) return this._spherical.x;
        if (isOrthographicCamera(this.entity.camera)) return this._spherical.x;

        const boundingRectAspect = width / height;
        const fov = this.entity.camera.fov * DEG2RAD;
        const aspect = this.entity.camera.aspectRatio;

        const heightToFit = (cover ? boundingRectAspect > aspect : boundingRectAspect < aspect) ? height : width / aspect;
        return heightToFit * 0.5 / Math.tan(fov * 0.5) + depth * 0.5;
    }

    /**
     * Calculate the distance to fit the sphere.
     * @param radius sphere radius
     * @returns distance
     */
    public getDistanceToFitSphere(radius: number): number {
        if (!this.entity.camera) return this._spherical.x;
        if (isOrthographicCamera(this.entity.camera)) return this._spherical.x;

        const vFOV = this.entity.camera.fov * DEG2RAD;
        const hFOV = Math.atan(Math.tan(vFOV * 0.5) * this.entity.camera.aspectRatio) * 2;
        const fov = 1 < this.entity.camera.aspectRatio ? vFOV : hFOV;
        return radius / Math.sin(fov * 0.5);
    }

    /**
     * Fit the camera to view a bounding box.
     * @param aabb The bounding box to fit
     * @param enableTransition Whether to animate the transition
     * @param options Fit options including padding
     */
    public fitToBox(aabb: pc.BoundingBox, enableTransition = true, options: FitToOptions = { cover: false, paddingTop: 0, paddingRight: 0, paddingBottom: 0, paddingLeft: 0 }): Promise<void> {
        if (!this.entity.camera) return Promise.resolve();

        const { cover = false, paddingLeft = 0, paddingRight = 0, paddingBottom = 0, paddingTop = 0 } = options;
        
        const center = aabb.center;
        const size = aabb.halfExtents.mulScalar(2); // Full extents

        // Apply padding (approximate)
        const paddedWidth = size.x + paddingLeft + paddingRight;
        const paddedHeight = size.y + paddingTop + paddingBottom;
        const paddedDepth = size.z;

        const promises: Promise<void>[] = [];

        promises.push(this.moveTo(center.x, center.y, center.z, enableTransition));

        if (isPerspectiveCamera(this.entity.camera)) {
            const distance = this.getDistanceToFitBox(paddedWidth, paddedHeight, paddedDepth, cover);
            promises.push(this.dollyTo(distance, enableTransition));
        } else if (isOrthographicCamera(this.entity.camera)) {
            const width = this.entity.camera.orthoHeight * this.entity.camera.aspectRatio;
            const height = this.entity.camera.orthoHeight;
            const zoom = cover ? Math.max(width / paddedWidth, height / paddedHeight) : Math.min(width / paddedWidth, height / paddedHeight);
            promises.push(this.zoomTo(zoom, enableTransition));
        }
        
        promises.push(this.setFocalOffset(0, 0, 0, enableTransition));

        return Promise.all(promises).then(() => void 0);
    }

    public fitToSphere(sphere: { center: pc.Vec3, radius: number }, enableTransition = true): Promise<void> {
        if (!this.entity.camera) return Promise.resolve();

        const promises: Promise<void>[] = [];
        
        promises.push(this.moveTo(sphere.center.x, sphere.center.y, sphere.center.z, enableTransition));

        if (isPerspectiveCamera(this.entity.camera)) {
            const distance = this.getDistanceToFitSphere(sphere.radius);
            promises.push(this.dollyTo(distance, enableTransition));
        } else if (isOrthographicCamera(this.entity.camera)) {
            const width = this.entity.camera.orthoHeight * this.entity.camera.aspectRatio;
            const height = this.entity.camera.orthoHeight;
            const diameter = 2 * sphere.radius;
            const zoom = Math.min(width / diameter, height / diameter);
            promises.push(this.zoomTo(zoom, enableTransition));
        }

        promises.push(this.setFocalOffset(0, 0, 0, enableTransition));

        return Promise.all(promises).then(() => void 0);
    }

    public setLookAt(positionX: number, positionY: number, positionZ: number, targetX: number, targetY: number, targetZ: number, enableTransition = false): Promise<void> {
        this._isUserControllingRotate = false;
        this._isUserControllingDolly = false;
        this._isUserControllingTruck = false;
        this._lastDollyDirection = DOLLY_DIRECTION.NONE;
        this._changedDolly = 0;

        this._targetEnd.set(targetX, targetY, targetZ);
        const position = this._tempVec.set(positionX, positionY, positionZ);
        
        // Calculate spherical from new position and target
        const offset = position.sub(this._targetEnd);
        
        this._yAxisUpSpace.transformVector(offset, offset);

        const radius = offset.length();
        if (radius > 0) offset.divScalar(radius);
        
        this._sphericalEnd.x = radius;
        this._sphericalEnd.y = Math.acos(clamp(offset.y, -1, 1));
        this._sphericalEnd.z = Math.atan2(offset.x, offset.z);
        
        if (!enableTransition) {
            this._target.copy(this._targetEnd);
            this._spherical.copy(this._sphericalEnd);
            this._updatePositionFromSpherical();
        }
        this.dispatchEvent({ type: 'control' });

        const resolveImmediately = !enableTransition ||
            approxZero(this._target.x - this._targetEnd.x, this.restThreshold) &&
            approxZero(this._target.y - this._targetEnd.y, this.restThreshold) &&
            approxZero(this._target.z - this._targetEnd.z, this.restThreshold) &&
            approxZero(this._spherical.z - this._sphericalEnd.z, this.restThreshold) &&
            approxZero(this._spherical.y - this._sphericalEnd.y, this.restThreshold) &&
            approxZero(this._spherical.x - this._sphericalEnd.x, this.restThreshold);
            
        return this._createOnRestPromise(resolveImmediately);
    }

    public lerp(stateA: CameraControlsLerpState, stateB: CameraControlsLerpState, t: number, enableTransition = false): Promise<void> {
        this._isUserControllingRotate = false;
        this._isUserControllingDolly = false;
        this._isUserControllingTruck = false;
        this._lastDollyDirection = DOLLY_DIRECTION.NONE;
        this._changedDolly = 0;

        const targetA = this._tempVec.set(stateA.target[0], stateA.target[1], stateA.target[2]);
        const sphericalA = this._tempVec2; // Reuse tempVec2 for sphericalA

        if ('spherical' in stateA) {
            sphericalA.set(stateA.spherical[0], stateA.spherical[1], stateA.spherical[2]);
        } else {
            const positionA = this._fromWorldPoint.set(stateA.position[0], stateA.position[1], stateA.position[2]);
            const offset = positionA.sub(targetA);
            this._yAxisUpSpace.transformVector(offset, offset);
            const radius = offset.length();
            if (radius > 0) offset.divScalar(radius);
            sphericalA.set(
                radius,
                Math.acos(clamp(offset.y, -1, 1)),
                Math.atan2(offset.x, offset.z)
            );
        }

        const targetB = this._toWorldPoint.set(stateB.target[0], stateB.target[1], stateB.target[2]);
        const sphericalB = this._worldDiff; // Reuse worldDiff for sphericalB

        if ('spherical' in stateB) {
            sphericalB.set(stateB.spherical[0], stateB.spherical[1], stateB.spherical[2]);
        } else {
            const positionB = this._tempVec3.set(stateB.position[0], stateB.position[1], stateB.position[2]);
            const offset = positionB.sub(targetB);
            this._yAxisUpSpace.transformVector(offset, offset);
            const radius = offset.length();
            if (radius > 0) offset.divScalar(radius);
            sphericalB.set(
                radius,
                Math.acos(clamp(offset.y, -1, 1)),
                Math.atan2(offset.x, offset.z)
            );
        }

        this._targetEnd.lerp(targetA, targetB, t);

        const deltaTheta = sphericalB.z - sphericalA.z;
        const deltaPhi = sphericalB.y - sphericalA.y;
        const deltaRadius = sphericalB.x - sphericalA.x;

        this._sphericalEnd.set(
            sphericalA.x + deltaRadius * t,
            sphericalA.y + deltaPhi * t,
            sphericalA.z + deltaTheta * t
        );

        if (!enableTransition) {
            this._target.copy(this._targetEnd);
            this._spherical.copy(this._sphericalEnd);
            this._updatePositionFromSpherical();
        }
        this.dispatchEvent({ type: 'control' });

        const resolveImmediately = !enableTransition ||
            approxZero(this._target.x - this._targetEnd.x, this.restThreshold) &&
            approxZero(this._target.y - this._targetEnd.y, this.restThreshold) &&
            approxZero(this._target.z - this._targetEnd.z, this.restThreshold) &&
            approxZero(this._spherical.z - this._sphericalEnd.z, this.restThreshold) &&
            approxZero(this._spherical.y - this._sphericalEnd.y, this.restThreshold) &&
            approxZero(this._spherical.x - this._sphericalEnd.x, this.restThreshold);
            
        return this._createOnRestPromise(resolveImmediately);
    }

    public lerpLookAt(
        positionAX: number, positionAY: number, positionAZ: number,
        targetAX: number, targetAY: number, targetAZ: number,
        positionBX: number, positionBY: number, positionBZ: number,
        targetBX: number, targetBY: number, targetBZ: number,
        t: number,
        enableTransition = false
    ): Promise<void> {
        return this.lerp(
            {
                position: [positionAX, positionAY, positionAZ],
                target: [targetAX, targetAY, targetAZ],
            },
            {
                position: [positionBX, positionBY, positionBZ],
                target: [targetBX, targetBY, targetBZ],
            },
            t,
            enableTransition
        );
    }

    public setPosition(x: number, y: number, z: number, enableTransition = false): Promise<void> {
        return this.setLookAt(x, y, z, this._targetEnd.x, this._targetEnd.y, this._targetEnd.z, enableTransition);
    }

    public setFocalOffset(x: number, y: number, z: number, enableTransition = false): Promise<void> {
        const resolveImmediately = this._setFocalOffsetInternal(x, y, z, enableTransition);
        this.dispatchEvent({ type: 'control' });
        return this._createOnRestPromise(resolveImmediately);
    }

    private _setFocalOffsetInternal(x: number, y: number, z: number, enableTransition = false): boolean {
        this._focalOffsetEnd.set(x, y, z);
        
        if (!enableTransition) {
            this._focalOffset.copy(this._focalOffsetEnd);
        }

        return !enableTransition ||
            approxZero(this._focalOffset.x - this._focalOffsetEnd.x, this.restThreshold) &&
            approxZero(this._focalOffset.y - this._focalOffsetEnd.y, this.restThreshold) &&
            approxZero(this._focalOffset.z - this._focalOffsetEnd.z, this.restThreshold);
    }

    public setOrbitPoint(targetX: number, targetY: number, targetZ: number) {
        const position = this._tempVec.set(targetX, targetY, targetZ);
        const distance = position.distance(this._position);
        const cameraToPoint = position.sub(this._position);
        
        // We need camera local axes.
        // PlayCanvas entity.right, entity.up, entity.forward
        const right = this.entity.right;
        const up = this.entity.up;
        const forward = this.entity.forward;

        const x = right.dot(cameraToPoint);
        const y = up.dot(cameraToPoint);
        const z = forward.dot(cameraToPoint);

        const offset = this._tempVec2.set(x, y, z + distance);
        
        this.dollyTo(distance, false);
        this.setFocalOffset(-offset.x, offset.y, -offset.z, false);
        this.moveTo(targetX, targetY, targetZ, false);
    }

    public setBoundary(box?: pc.BoundingBox) {
        if (!box) {
            this._boundary.center.set(0, 0, 0);
            this._boundary.halfExtents.set(Infinity, Infinity, Infinity);
        } else {
            this._boundary.copy(box);
            // Clamp current target
            const clamped = this._tempVec.copy(this._targetEnd);
            const min = this._boundary.getMin();
            const max = this._boundary.getMax();
            clamped.x = pc.math.clamp(clamped.x, min.x, max.x);
            clamped.y = pc.math.clamp(clamped.y, min.y, max.y);
            clamped.z = pc.math.clamp(clamped.z, min.z, max.z);
            this._targetEnd.copy(clamped);
        }
        this.dispatchEvent({ type: 'control' });
    }

    public setViewport(viewportOrX: pc.Vec4 | number | null, y?: number, width?: number, height?: number) {
        if (viewportOrX === null) {
            this._viewport = null;
            return;
        }

        this._viewport = this._viewport || new pc.Vec4();

        if (typeof viewportOrX === 'number') {
            if (y !== undefined && width !== undefined && height !== undefined) {
                this._viewport.set(viewportOrX, y, width, height);
            }
        } else {
            this._viewport.copy(viewportOrX);
        }
    }

    public lookInDirectionOf(x: number, y: number, z: number, enableTransition = false): Promise<void> {
        const point = this._tempVec.set(x, y, z);
        const direction = point.sub(this._targetEnd).normalize();
        const position = direction.mulScalar(-this._sphericalEnd.x).add(this._targetEnd);
        return this.setPosition(position.x, position.y, position.z, enableTransition);
    }

    public saveState() {
        this._target0.copy(this._targetEnd);
        this._position0.copy(this._position); // Approximate
        this._zoom0 = this._zoomEnd;
        this._focalOffset0.copy(this._focalOffsetEnd);
    }

    public reset(enableTransition = false): Promise<void> {
        if (!approxZero(this.entity.up.x - this._cameraUp0.x) ||
            !approxZero(this.entity.up.y - this._cameraUp0.y) ||
            !approxZero(this.entity.up.z - this._cameraUp0.z)) {
            
            this.entity.up.copy(this._cameraUp0);
            this._updateSpherical();
        }

        const promises = [
            this.setLookAt(
                this._position0.x, this._position0.y, this._position0.z,
                this._target0.x, this._target0.y, this._target0.z,
                enableTransition
            ),
            this.setFocalOffset(this._focalOffset0.x, this._focalOffset0.y, this._focalOffset0.z, enableTransition),
            this.zoomTo(this._zoom0, enableTransition)
        ];
        
        return Promise.all(promises).then(() => void 0);
    }
    
    public normalizeRotations(): CameraControls {
        const PI_2 = 2 * Math.PI;
        this._sphericalEnd.z = ((this._sphericalEnd.z % PI_2) + PI_2) % PI_2;
        if (this._sphericalEnd.z > Math.PI) this._sphericalEnd.z -= PI_2;
        this._spherical.z += PI_2 * Math.round((this._sphericalEnd.z - this._spherical.z) / PI_2);
        return this;
    }

    public stop() {
        this._focalOffset.copy(this._focalOffsetEnd);
        this._target.copy(this._targetEnd);
        this._spherical.copy(this._sphericalEnd);
        this._zoom = this._zoomEnd;
    }

    public updateCameraUp() {
        setQuatFromUnitVectors(this._yAxisUpSpace, this.entity.up, pc.Vec3.UP);
        this._yAxisUpSpaceInverse.copy(this._yAxisUpSpace).invert();
    }

    public applyCameraUp() {
        const cameraDirection = this._tempVec.sub2(this._target, this._position).normalize();
        const side = this._tempVec2.cross(cameraDirection, this.entity.up);
        this.entity.up.cross(side, cameraDirection).normalize();
        
        // Re-sync
        const position = this.getPosition(this._tempVec);
        this.updateCameraUp();
        this.setPosition(position.x, position.y, position.z);
    }

    public getTarget(out: pc.Vec3 = new pc.Vec3(), receiveEndValue = true): pc.Vec3 {
        return out.copy(receiveEndValue ? this._targetEnd : this._target);
    }

    public getPosition(out: pc.Vec3 = new pc.Vec3(), receiveEndValue = true): pc.Vec3 {
        if (receiveEndValue) {
            // Calculate position from sphericalEnd and targetEnd
            const sinPhiRadius = this._sphericalEnd.x * Math.sin(this._sphericalEnd.y);
            out.x = sinPhiRadius * Math.sin(this._sphericalEnd.z);
            out.y = this._sphericalEnd.x * Math.cos(this._sphericalEnd.y);
            out.z = sinPhiRadius * Math.cos(this._sphericalEnd.z);
            
            this._yAxisUpSpace.transformVector(out, out);
            
            out.add(this._targetEnd);
            return out;
        } else {
            return out.copy(this._position);
        }
    }

    public getSpherical(out: pc.Vec3 = new pc.Vec3(), receiveEndValue = true): pc.Vec3 {
        return out.copy(receiveEndValue ? this._sphericalEnd : this._spherical);
    }

    public getFocalOffset(out: pc.Vec3 = new pc.Vec3(), receiveEndValue = true): pc.Vec3 {
        return out.copy(receiveEndValue ? this._focalOffsetEnd : this._focalOffset);
    }
    
    public toJSON(): string {
        return JSON.stringify({
            minDistance: this.minDistance,
            maxDistance: this.maxDistance,
            minPolarAngle: this.minPolarAngle,
            maxPolarAngle: this.maxPolarAngle,
            minAzimuthAngle: this.minAzimuthAngle,
            maxAzimuthAngle: this.maxAzimuthAngle,
            enableDamping: this.enableDamping,
            dampingFactor: this.dampingFactor,
            alignRotationToTarget: this.alignRotationToTarget,
            zoom: this._zoomEnd,
            target: [this._targetEnd.x, this._targetEnd.y, this._targetEnd.z],
            position: [this._position.x, this._position.y, this._position.z],
            focalOffset: [this._focalOffsetEnd.x, this._focalOffsetEnd.y, this._focalOffsetEnd.z]
        });
    }
    
    public fromJSON(json: string, enableTransition = false) {
        const data = JSON.parse(json);
        this.minDistance = data.minDistance;
        this.maxDistance = data.maxDistance;
        this.minPolarAngle = data.minPolarAngle;
        this.maxPolarAngle = data.maxPolarAngle;
        this.minAzimuthAngle = data.minAzimuthAngle;
        this.maxAzimuthAngle = data.maxAzimuthAngle;
        this.enableDamping = data.enableDamping;
        this.alignRotationToTarget = data.alignRotationToTarget;
        this.dampingFactor = data.dampingFactor;
        
        if (data.target && data.position) {
            this.setLookAt(
                data.position[0], data.position[1], data.position[2],
                data.target[0], data.target[1], data.target[2],
                enableTransition
            );
        }
        
        if (data.zoom) {
            this._zoomEnd = data.zoom;
            if (!enableTransition) this._zoom = data.zoom;
        }
        
        if (data.focalOffset) {
            this.setFocalOffset(data.focalOffset[0], data.focalOffset[1], data.focalOffset[2], enableTransition);
        }
    }

    public get target(): pc.Vec3 {
        return this._target;
    }

    public get position(): pc.Vec3 {
        return this._position;
    }

    public get azimuth(): number {
        return this._spherical.z;
    }

    public get polar(): number {
        return this._spherical.y;
    }

    public get distance(): number {
        return this._spherical.x;
    }

    private _shouldCommitChange(
        lastPos: pc.Vec3,
        lastRot: pc.Quat,
        lastZoom: number,
        candidatePos: pc.Vec3,
        candidateRot: pc.Quat,
        candidateZoom: number,
        camera: pc.CameraComponent,
        distanceToTarget: number
    ): boolean {
        // Fix 4: Pixel thresholds must be camera-relative, not viewport-relative
        // Use projection-space epsilon, not pixels
        let epsilonWorld = 1e-5; // Fallback
        
        if (isPerspectiveCamera(camera)) {
            const fovRad = camera.fov * Math.PI / 180;
            epsilonWorld = distanceToTarget * Math.tan(fovRad * 0.5) * 1e-4;
        } else if (isOrthographicCamera(camera)) {
            epsilonWorld = camera.orthoHeight * 1e-4;
        }

        // Position Delta
        const posDelta = candidatePos.distance(lastPos);
        
        // Fix 3: Rotation metric is over-sensitive near rest
        // Use 1 - dot, which is stable
        const rotMetric = 1 - Math.abs(candidateRot.dot(lastRot));
        // Approximate arc length at target distance
        // For small angles, sin(theta) ~= theta. 
        // dot = cos(theta/2). 1-dot = 1-cos(theta/2) ~= (theta/2)^2 / 2
        // This metric is non-linear but monotonic.
        // Let's use the user's suggestion directly if possible, but they didn't give the full formula for "PIXELS_PER_RAD".
        // Instead, let's map this metric to a world distance at the target.
        // If we rotate by 'theta', the arc length at distance 'd' is theta * d.
        // We want to compare this arc length to epsilonWorld.
        
        // Let's stick to the user's "Fix 4" instruction: "Compare world deltas directly".
        // We need to convert rotation change to a world delta at the target distance.
        // 1 - dot is roughly proportional to angle squared for small angles.
        // Angle ~= 2 * sqrt(2 * (1 - dot))
        const rotAngleRad = 2 * Math.sqrt(2 * rotMetric); 
        const rotArcLength = rotAngleRad * distanceToTarget;

        // Zoom Delta (Ortho only)
        let zoomDelta = 0;
        if (isOrthographicCamera(camera)) {
            zoomDelta = Math.abs(lastZoom - candidateZoom);
        }

        // Hysteresis Check
        // We use epsilonWorld as the base unit.
        // Wake when > 2 * epsilonWorld
        // Sleep when < 0.5 * epsilonWorld
        const wakeThreshold = epsilonWorld * 2.0;
        const sleepThreshold = epsilonWorld * 0.5;
        
        const threshold = this._isSleeping ? wakeThreshold : sleepThreshold;
        const isSignificant = posDelta > threshold || rotArcLength > threshold || zoomDelta > threshold;

        if (isSignificant) {
            this._isSleeping = false;
            return true;
        } else {
            // If we are below sleep threshold, we consider it sleeping
            if (posDelta < sleepThreshold && rotArcLength < sleepThreshold && zoomDelta < sleepThreshold) {
                this._isSleeping = true;
            }
            return false;
        }
    }

    public update(dt: number): boolean {
        // Use raw dt. Smoothing can cause desync with PlayCanvas render loop.
        const delta = dt;
        const isDragging = this._activePointers.length > 0;

        // 1. Update Instantaneous State from Inputs
        this._spherical.copy(this._sphericalEnd);
        this._target.copy(this._targetEnd);
        this._focalOffset.copy(this._focalOffsetEnd);
        
        if (this.autoRotate && !this._isUserControllingRotate && !this._isUserControllingTruck && !this._isUserControllingDolly && !this._isUserControllingZoom) {
            this._spherical.z += this.autoRotateSpeed * delta;
            this._sphericalEnd.z = this._spherical.z;
        }

        // 2. Handle Dolly To Cursor (modifies _targetEnd)
        if (this.dollyToCursor) {
            if (isPerspectiveCamera(this.entity.camera!) && this._spherical.x !== this._lastSpherical.x) {
                const dollyControlAmount = this._spherical.x - this._lastSpherical.x;
                const cameraDirection = this._tempVec.sub2(this._target, this._position).normalize();
                const up = this.entity.up;
                const planeX = this._tempVec2.cross(cameraDirection, up).normalize();
                if (planeX.lengthSq() === 0) planeX.x = 1.0;
                const planeY = this._fromWorldPoint.cross(planeX, cameraDirection);
                
                const fov = this.entity.camera!.fov * DEG2RAD;
                const worldToScreen = this._sphericalEnd.x * Math.tan(fov * 0.5);
                const prevRadius = this._sphericalEnd.x - dollyControlAmount;
                const lerpRatio = (prevRadius - this._sphericalEnd.x) / this._sphericalEnd.x;
                
                const cursor = this._toWorldPoint.copy(this._targetEnd)
                    .add(planeX.mulScalar(this._dollyControlCoord.x * worldToScreen * this.entity.camera!.aspectRatio))
                    .add(planeY.mulScalar(this._dollyControlCoord.y * worldToScreen));
                
                const newTargetEnd = this._worldDiff.lerp(this._targetEnd, cursor, lerpRatio);

                const isMin = this._lastDollyDirection === DOLLY_DIRECTION.IN && this._spherical.x <= this.minDistance;
                const isMax = this._lastDollyDirection === DOLLY_DIRECTION.OUT && this.maxDistance <= this._spherical.x;

                if (this.infinityDolly && (isMin || isMax)) {
                    this._sphericalEnd.x -= dollyControlAmount;
                    this._spherical.x -= dollyControlAmount;
                    const dollyAmount = this._tempVec.copy(cameraDirection).mulScalar(-dollyControlAmount);
                    newTargetEnd.add(dollyAmount);
                }

                const targetEndDiff = this._tempVec.sub2(newTargetEnd, this._targetEnd);
                this._targetEnd.copy(newTargetEnd);
                this._target.add(targetEndDiff);

                this._changedDolly -= dollyControlAmount;
                if (approxZero(this._changedDolly)) this._changedDolly = 0;
            } else if (isOrthographicCamera(this.entity.camera!) && this._changedZoom !== 0) {
                 this._changedZoom = 0;
            }
        }

        // 3. Collision Test (modifies _spherical.x)
        this._collisionTest();

        // 4. Calculate Desired Pose
        const theta = this._spherical.z;
        const phi = pc.math.clamp(this._spherical.y, 1e-5, Math.PI - 1e-5);
        const radius = this._spherical.x;

        const sinPhiRadius = radius * Math.sin(phi);
        
        this._desiredPos.x = sinPhiRadius * Math.sin(theta);
        this._desiredPos.y = Math.cos(phi) * radius;
        this._desiredPos.z = sinPhiRadius * Math.cos(theta);

        this._yAxisUpSpaceInverse.transformVector(this._desiredPos, this._desiredPos);
        this._desiredPos.add(this._target);
        
        if (this._boundaryEnclosesCamera) {
             const offset = this._tempVec.sub2(this._desiredPos, this._target);
             this._encloseToBoundary(this._target, offset, 1.0);
             this._desiredPos.copy(this._target).add(offset);
        }

        // Calculate Desired Rotation
        this._tempMat.setLookAt(this._desiredPos, this._target, pc.Vec3.UP);
        this._desiredRot.setFromMat4(this._tempMat);
        
        // Add Focal Offset to Desired Position (in View Space of Desired Rotation)
        if (!approxZero(this._focalOffset.lengthSq())) {
            const worldOffset = this._tempVec3;
            this._desiredRot.transformVector(this._focalOffset, worldOffset);
            this._desiredPos.add(worldOffset);
        }

        // 5. Damp Pose
        const baseSmoothTime = this.enableDamping ? this.smoothTime : 0;
        const baseDraggingSmoothTime = this.enableDamping ? this.draggingSmoothTime : 0;
        const smoothTime = isDragging ? baseDraggingSmoothTime : baseSmoothTime;

        // Damp Position
        if (smoothTime < 1e-4) {
            this._position.copy(this._desiredPos);
            this._cameraPosVelocity.set(0, 0, 0);
        } else {
            smoothDampVec3(this._position, this._desiredPos, this._cameraPosVelocity, smoothTime, this.maxSpeed, delta, this._position);
            if (this._cameraPosVelocity.lengthSq() < 1e-10) this._cameraPosVelocity.set(0, 0, 0);
        }

        // Damp Rotation
        if (this.alignRotationToTarget) {
            this._tempMat.setLookAt(this._position, this._target, pc.Vec3.UP);
            this.rotation.setFromMat4(this._tempMat);
            this._cameraRotVelocity.set(0, 0, 0, 0);
        } else if (smoothTime < 1e-4) {
            this.rotation.copy(this._desiredRot);
            this._cameraRotVelocity.set(0, 0, 0, 0);
        } else {
            smoothDampQuat(this.rotation, this._desiredRot, this._cameraRotVelocity, smoothTime, delta, this.rotation);
            const velSq = this._cameraRotVelocity.x**2 + this._cameraRotVelocity.y**2 + this._cameraRotVelocity.z**2 + this._cameraRotVelocity.w**2;
            if (velSq < 1e-10) this._cameraRotVelocity.set(0, 0, 0, 0);
        }

        // Damp Zoom
        const zoomSmoothTime = this._isUserControllingZoom ? baseDraggingSmoothTime : baseSmoothTime;
        if (zoomSmoothTime < 1e-4) {
            this._zoom = this._zoomEnd;
            this._cameraZoomVelocity.value = 0;
        } else {
            this._zoom = smoothDamp(this._zoom, this._zoomEnd, this._cameraZoomVelocity, zoomSmoothTime, Infinity, delta);
            if (Math.abs(this._cameraZoomVelocity.value) < 1e-5) this._cameraZoomVelocity.value = 0;
        }

        // 6. Apply to Entity
        if (!isDragging) {
            this._isUserControllingRotate = false;
            this._isUserControllingTruck = false;
            this._isUserControllingDolly = false;
            this._isUserControllingZoom = false;
        }

        const app = pc.Application.getApplication();
        if (app && this.entity.camera) {
            // Always write transforms
            this.entity.setPosition(this._position);
            this.entity.setRotation(this.rotation);
            
            if (isOrthographicCamera(this.entity.camera)) {
                this.entity.camera.orthoHeight = this._zoom;
            }

            // Commit Gate
            const shouldCommit = this._shouldCommitChange(
                this._committedPos,
                this._committedRot,
                this._committedZoom,
                this._position,
                this.rotation,
                this._zoom,
                this.entity.camera,
                this._spherical.x
            );

            if (shouldCommit) {
                this._committedPos.copy(this._position);
                this._committedRot.copy(this.rotation);
                this._committedZoom = this._zoom;
                this._lastRotation.copy(this.rotation);
            }
        }

        // Update Last State
        this._lastSpherical.copy(this._spherical);
        this._lastTarget.copy(this._target);
        this._lastFocalOffset.copy(this._focalOffset);

        // Check Rest
        const isResting = 
            approxZero(this._cameraPosVelocity.lengthSq(), 1e-10) &&
            approxZero(this._cameraRotVelocity.x, 1e-5) &&
            approxZero(this._cameraRotVelocity.y, 1e-5) &&
            approxZero(this._cameraRotVelocity.z, 1e-5) &&
            approxZero(this._cameraRotVelocity.w, 1e-5) &&
            approxZero(this._cameraZoomVelocity.value, 1e-5);

        if (!isResting) {
            this._hasRested = false;
            this.dispatchEvent({ type: 'wake' });
            this.dispatchEvent({ type: 'update' });
        } else if (!this._hasRested) {
            this._hasRested = true;
            this.dispatchEvent({ type: 'update' });
        }
        
        return !isResting;
    }


    private _updatePositionFromSpherical() {
        const theta = this._spherical.z;
        // Clamp phi to avoid singularity at poles (0 and PI) which breaks lookAt
        const phi = pc.math.clamp(this._spherical.y, 1e-5, Math.PI - 1e-5);
        const radius = this._spherical.x;

        const sinPhiRadius = radius * Math.sin(phi);

        this._position.x = sinPhiRadius * Math.sin(theta);
        this._position.y = Math.cos(phi) * radius;
        this._position.z = sinPhiRadius * Math.cos(theta);

        // Apply up-vector rotation
        this._yAxisUpSpaceInverse.transformVector(this._position, this._position);

        this._position.add(this._target);

        // Update rotation
        this._tempMat.setLookAt(this._position, this._target, pc.Vec3.UP);
        this.rotation.setFromMat4(this._tempMat);
        
        // Add Focal Offset
        if (!approxZero(this._focalOffset.lengthSq())) {
            const worldOffset = this._tempVec3;
            this.rotation.transformVector(this._focalOffset, worldOffset);
            this._position.add(worldOffset);
        }
    }

    private _updateSpherical() {
        // Calculate spherical coords from current position/target
        const offset = this._tempVec.sub2(this._position, this._target);
        
        // Apply inverse up-vector rotation to align with Y-up space for spherical calc
        this._yAxisUpSpace.transformVector(offset, offset);

        const radius = offset.length();
        
        this._spherical.x = radius;
        
        if (radius === 0) {
            this._spherical.y = 0;
            this._spherical.z = 0;
        } else {
            this._spherical.y = Math.acos(pc.math.clamp(offset.y / radius, -1, 1)); // Phi (Polar)
            this._spherical.z = Math.atan2(offset.x, offset.z); // Theta (Azimuth)
        }
    }

    private _updateStateFromPointers() {
        this._state = ACTION.NONE;
        
        // Count touches (pointers with null mouseButton)
        let touchCount = 0;
        for (let i = 0; i < this._activePointers.length; i++) {
            if (this._activePointers[i].mouseButton === null) {
                touchCount++;
            }
        }
        
        if (touchCount > 0) {
            switch (touchCount) {
                case 1: this._state = this.touches.one; break;
                case 2: this._state = this.touches.two; break;
                case 3: this._state = this.touches.three; break;
            }
        }
        
        // Mouse
        for (const p of this._activePointers) {
            if (p.mouseButton === MOUSE_BUTTON.LEFT) this._state |= this.mouseButtons.left;
            if (p.mouseButton === MOUSE_BUTTON.MIDDLE) this._state |= this.mouseButtons.middle;
            if (p.mouseButton === MOUSE_BUTTON.RIGHT) this._state |= this.mouseButtons.right;
        }

        // Update controlling flags based on state
        if (this._state & ACTION.ROTATE || this._state & ACTION.TOUCH_ROTATE) this._isUserControllingRotate = true;
        if (this._state & ACTION.TRUCK || this._state & ACTION.TOUCH_TRUCK || this._state & ACTION.TOUCH_DOLLY_TRUCK) this._isUserControllingTruck = true;
        if (this._state & ACTION.DOLLY || this._state & ACTION.TOUCH_DOLLY || this._state & ACTION.TOUCH_DOLLY_TRUCK) this._isUserControllingDolly = true;
        if (this._state & ACTION.ZOOM || this._state & ACTION.TOUCH_ZOOM) this._isUserControllingZoom = true;
    }

    // --- Internal Event Handlers ---
    private _onPointerDown = (event: PointerEvent) => {
        if (!this._enabled) return;
        if (!this._domElement) return;

        if (this._interactiveArea.x !== 0 || this._interactiveArea.y !== 0 || this._interactiveArea.width !== 1 || this._interactiveArea.height !== 1) {
            const rect = this._domElement.getBoundingClientRect();
            const localX = event.clientX - rect.left;
            const localY = event.clientY - rect.top;
            const nx = localX / rect.width;
            const ny = localY / rect.height;
            
            if (nx < this._interactiveArea.x || nx > this._interactiveArea.x + this._interactiveArea.width ||
                ny < this._interactiveArea.y || ny > this._interactiveArea.y + this._interactiveArea.height) {
                return;
            }
        }

        let mouseButton: MOUSE_BUTTON | null = null;
        if (event.pointerType === 'mouse') {
            if (event.button === 0) mouseButton = MOUSE_BUTTON.LEFT;
            else if (event.button === 1) mouseButton = MOUSE_BUTTON.MIDDLE;
            else if (event.button === 2) mouseButton = MOUSE_BUTTON.RIGHT;

            if (mouseButton === null) {
                mouseButton = 
                    (event.buttons & MOUSE_BUTTON.LEFT) === MOUSE_BUTTON.LEFT ? MOUSE_BUTTON.LEFT :
                    (event.buttons & MOUSE_BUTTON.MIDDLE) === MOUSE_BUTTON.MIDDLE ? MOUSE_BUTTON.MIDDLE :
                    (event.buttons & MOUSE_BUTTON.RIGHT) === MOUSE_BUTTON.RIGHT ? MOUSE_BUTTON.RIGHT :
                    null;
            }
        }

        if (mouseButton !== null) {
            const zombiePointer = this._findPointerByMouseButton(mouseButton);
            if (zombiePointer) this._disposePointer(zombiePointer);
        }

        const pointer: PointerInput = {
            pointerId: event.pointerId,
            clientX: event.clientX,
            clientY: event.clientY,
            deltaX: 0,
            deltaY: 0,
            mouseButton
        };
        
        this._activePointers.push(pointer);
        this._updateStateFromPointers();
        
        this._domElement.setPointerCapture(event.pointerId);
        this._domElement.addEventListener('pointermove', this._onPointerMove, { passive: false });
        this._domElement.addEventListener('pointerup', this._onPointerUp);
        
        this._startDragging(event);
    }

    private _onPointerMove = (event: PointerEvent) => {
        const pointer = this._findPointerById(event.pointerId);
        if (!pointer) return;
        
        pointer.deltaX = event.clientX - pointer.clientX;
        pointer.deltaY = event.clientY - pointer.clientY;
        pointer.clientX = event.clientX;
        pointer.clientY = event.clientY;
        
        // Update state based on active pointers
        this._updateStateFromPointers();
        
        this._dragging();
    }

    private _onPointerUp = (event: PointerEvent) => {
        const pointer = this._findPointerById(event.pointerId);
        if (pointer) this._disposePointer(pointer);
        
        if (this._domElement) {
            this._domElement.releasePointerCapture(event.pointerId);
        }

        this._updateStateFromPointers();

        if (this._activePointers.length === 0) {
            this._state = ACTION.NONE;
            this._isUserControllingRotate = false;
            this._isUserControllingTruck = false;
            this._isUserControllingDolly = false;
            this._isUserControllingZoom = false;

            if (this._domElement) {
                this._domElement.removeEventListener('pointermove', this._onPointerMove);
                this._domElement.removeEventListener('pointerup', this._onPointerUp);
            }
            this.dispatchEvent({ type: 'controlend' });
        } else {
            // Reset drag position to new centroid to avoid jumps when number of fingers changes
            extractClientCoordFromEvent(this._activePointers, this._dragStartPosition);
            this._lastDragPosition.copy(this._dragStartPosition);
            
            // Also reset dolly start if we still have 2 pointers (e.g. went from 3 to 2)
            if (this._activePointers.length >= 2) {
                const p1 = this._activePointers[0];
                const p2 = this._activePointers[1];
                const dx = p1.clientX - p2.clientX;
                const dy = p1.clientY - p2.clientY;
                const distance = Math.sqrt(dx*dx + dy*dy);
                this._dollyStart.set(0, distance);
            }
        }
    }

    private _onWheel = (event: WheelEvent) => {
        if (!this._enabled) return;
        if (!this._domElement) return;

        if (this._interactiveArea.x !== 0 || this._interactiveArea.y !== 0 || this._interactiveArea.width !== 1 || this._interactiveArea.height !== 1) {
            const rect = this._domElement.getBoundingClientRect();
            const localX = event.clientX - rect.left;
            const localY = event.clientY - rect.top;
            const nx = localX / rect.width;
            const ny = localY / rect.height;
            
            if (nx < this._interactiveArea.x || nx > this._interactiveArea.x + this._interactiveArea.width ||
                ny < this._interactiveArea.y || ny > this._interactiveArea.y + this._interactiveArea.height) {
                return;
            }
        }

        event.preventDefault();

        const delta = event.deltaY;
        
        switch (this.mouseButtons.wheel) {
            case ACTION.DOLLY:
                this._dollyInternal(delta * 0.01, event.clientX, event.clientY);
                this._isUserControllingDolly = true;
                break;
            case ACTION.ZOOM:
                this._zoomInternal(delta * 0.01, event.clientX, event.clientY);
                this._isUserControllingZoom = true;
                break;
            case ACTION.ROTATE:
                this._rotateInternal(event.deltaX, event.deltaY);
                this._isUserControllingRotate = true;
                break;
            case ACTION.TRUCK:
                this._truckInternal(event.deltaX, event.deltaY, false, false);
                this._isUserControllingTruck = true;
                break;
            case ACTION.OFFSET:
                this._truckInternal(event.deltaX, event.deltaY, true, false);
                break;
        }
        
        this.dispatchEvent({ type: 'control' });
    }

    private _onContextMenu = (event: Event) => {
        if (!this._enabled) return;
        event.preventDefault();
    }

    // --- Internal Logic ---

    private _startDragging(event: PointerEvent) {
        extractClientCoordFromEvent(this._activePointers, this._dragStartPosition);
        this._lastDragPosition.copy(this._dragStartPosition);
        
        this._elementRect = this._domElement?.getBoundingClientRect() || null;

        if (this._activePointers.length >= 2) {
            const p1 = this._activePointers[0];
            const p2 = this._activePointers[1];
            const dx = p1.clientX - p2.clientX;
            const dy = p1.clientY - p2.clientY;
            const distance = Math.sqrt(dx*dx + dy*dy);
            this._dollyStart.set(0, distance);
        }

        this.dispatchEvent({ type: 'controlstart' });
    }

    private _dragging() {
        // Inline extraction to avoid GC and improve performance
        this._clientCoord.set(0, 0);
        let count = 0;
        for (let i = 0; i < this._activePointers.length; i++) {
            const p = this._activePointers[i];
            this._clientCoord.x += p.clientX;
            this._clientCoord.y += p.clientY;
            count++;
        }
        if (count > 0) {
            this._clientCoord.x /= count;
            this._clientCoord.y /= count;
        }
        
        const deltaX = this._lastDragPosition.x - this._clientCoord.x;
        const deltaY = this._lastDragPosition.y - this._clientCoord.y;
        
        this._lastDragPosition.copy(this._clientCoord);

        if (deltaX === 0 && deltaY === 0) return;

        if (this._state & ACTION.ROTATE || this._state & ACTION.TOUCH_ROTATE) {
            this._rotateInternal(deltaX, deltaY);
            this._isUserControllingRotate = true;
        }
        
        if (this._state & ACTION.TRUCK || this._state & ACTION.TOUCH_TRUCK || this._state & ACTION.TOUCH_DOLLY_TRUCK) {
            this._truckInternal(deltaX, deltaY, false, false);
            this._isUserControllingTruck = true;
        }

        if (this._state & ACTION.OFFSET || this._state & ACTION.TOUCH_OFFSET) {
            this._truckInternal(deltaX, deltaY, true, false);
        }

        if (this._state & ACTION.DOLLY) {
            this._dollyInternal(deltaY * 0.01, this._clientCoord.x, this._clientCoord.y);
            this._isUserControllingDolly = true;
        }
        
        if (this._activePointers.length >= 2) {
            const p1 = this._activePointers[0];
            const p2 = this._activePointers[1];
            const dx = p1.clientX - p2.clientX;
            const dy = p1.clientY - p2.clientY;
            const dist = Math.sqrt(dx*dx + dy*dy);
            
            const prevDist = this._dollyStart.y;
            const deltaDist = dist - prevDist;
            this._dollyStart.y = dist;
            
            if (this._state & ACTION.TOUCH_DOLLY || this._state & ACTION.TOUCH_DOLLY_TRUCK) {
                this.dolly(deltaDist * this.dollySpeed * 0.01);
                this._isUserControllingDolly = true;
            }
        }
        
        this.dispatchEvent({ type: 'control' });
    }

    private _rotateInternal(deltaX: number, deltaY: number) {
        if (!this._elementRect || this._elementRect.height === 0) return;
        this.alignRotationToTarget = true;
        const theta = 2 * PI * this.azimuthRotateSpeed * deltaX / this._elementRect.height;
        const phi = 2 * PI * this.polarRotateSpeed * deltaY / this._elementRect.height;
        // this.rotate(theta, phi, true);
        this._rotateToInternal(this._sphericalEnd.z + theta, this._sphericalEnd.y + phi, true);
    }

    private _getVerticalFOV(): number {
        const camera = this.entity.camera!;
        if (camera.horizontalFov) {
            const aspect = camera.aspectRatio;
            const hFov = camera.fov * DEG2RAD;
            return 2 * Math.atan(Math.tan(hFov * 0.5) / aspect);
        }
        return camera.fov * DEG2RAD;
    }

    private _truckHelper(x: number, y: number, enableTransition: boolean) {
        const right = this.entity.right;
        const up = this.entity.up;

        const moveX = this._tempVec.copy(right).mulScalar(-x);
        const moveY = this._tempVec2.copy(up).mulScalar(-y);

        const offset = moveX.add(moveY);
        const to = this._worldDiff.copy(this._targetEnd).add(offset);
        
        this._moveToInternal(to.x, to.y, to.z, enableTransition);
    }

    private _forwardHelper(distance: number, enableTransition: boolean) {
        const forward = this._tempVec.sub2(this._target, this._position).normalize();
        forward.mulScalar(distance);
        const to = this._tempVec2.copy(this._targetEnd).add(forward);
        
        this._moveToInternal(to.x, to.y, to.z, enableTransition);
    }

    private _truckInternal(deltaX: number, deltaY: number, dragToOffset: boolean, screenSpacePanning: boolean) {
        if (!this._elementRect || this._elementRect.height === 0) return;
        this.alignRotationToTarget = false;
        
        let truckX = 0;
        let truckY = 0;

        if (isPerspectiveCamera(this.entity.camera!)) {
            const distance = this._spherical.x;
            const fov = this._getVerticalFOV();
            const visibleHeight = 2 * distance * Math.tan(fov * 0.5);
            
            truckX = -0.5 * this.truckSpeed * deltaX * visibleHeight / this._elementRect.height;
            truckY = 0.5 * this.truckSpeed * deltaY * visibleHeight / this._elementRect.height;
        } else if (isOrthographicCamera(this.entity.camera!)) {
            const visibleHeight = this.entity.camera!.orthoHeight;
            truckX = -0.5 * this.truckSpeed * deltaX * visibleHeight / this._elementRect.height;
            truckY = 0.5 * this.truckSpeed * deltaY * visibleHeight / this._elementRect.height;
        }

        if (screenSpacePanning) {
            if (dragToOffset) {
                this._setFocalOffsetInternal(
                    this._focalOffsetEnd.x + truckX,
                    this._focalOffsetEnd.y,
                    this._focalOffsetEnd.z,
                    true
                );
            } else {
                this._truckHelper(truckX, 0, true);
            }
            this._forwardHelper(-truckY, true);
        } else {
            if (dragToOffset) {
                this._setFocalOffsetInternal(
                    this._focalOffsetEnd.x + truckX,
                    this._focalOffsetEnd.y + truckY,
                    this._focalOffsetEnd.z,
                    true
                );
            } else {
                this._truckHelper(truckX, truckY, true);
            }
        }
    }

    private _dollyInternal(delta: number, x: number, y: number) {
        const dollyScale = Math.pow(0.95, -delta * this.dollySpeed);
        const lastDistance = this._sphericalEnd.x;
        const distance = lastDistance * dollyScale;
        const clampedDistance = clamp(distance, this.minDistance, this.maxDistance);
        const overflowedDistance = clampedDistance - distance;
        
        if (this.infinityDolly && this.dollyToCursor) {
             this._dollyToInternal(distance - lastDistance, true);
        } else if (this.infinityDolly && !this.dollyToCursor) {
             this._dollyInFixedInternal(overflowedDistance, true);
             this._dollyToInternal(clampedDistance, true);
        } else {
             this._dollyToInternal(clampedDistance, true);
        }

        if (this.dollyToCursor) {
            this._changedDolly += (this.infinityDolly ? distance : clampedDistance) - lastDistance;
            this._dollyControlCoord.set(x, y);
        }
        
        this._lastDollyDirection = Math.sign(-delta) as DOLLY_DIRECTION;
    }

    private _zoomInternal(delta: number, x: number, y: number) {
        const zoomScale = Math.pow(0.95, delta * this.dollySpeed);
        const lastZoom = this._zoomEnd;
        const zoom = lastZoom * zoomScale;

        this._zoomToInternal(zoom, true);

        if (this.dollyToCursor) {
            this._changedZoom += zoom - lastZoom;
            this._dollyControlCoord.set(x, y);
        }
    }

    private _collisionTest() {
        if (this.colliderMeshes.length === 0) return;
        
        let minDistance = this._spherical.x;
        let hit = false;

        // Calculate direction from spherical angles
        const phi = this._spherical.y;
        const theta = this._spherical.z;
        const sinPhi = Math.sin(phi);
        const dirX = sinPhi * Math.sin(theta);
        const dirY = Math.cos(phi);
        const dirZ = sinPhi * Math.cos(theta);
        
        const direction = this._tempVec.set(dirX, dirY, dirZ);
        this._yAxisUpSpace.transformVector(direction, direction); // Camera direction (from target)

        // We need to test 4 corners of the near plane to ensure the camera doesn't clip
        
        const up = this.entity.up;
        
        const mat = new pc.Mat4().setLookAt(pc.Vec3.ZERO, direction, up); 
        // setLookAt creates a view matrix (World -> View). We need View -> World (rotation only).
        mat.invert();

        for (let i = 0; i < 4; i++) {
            const corner = this._nearPlaneCorners[i];
            const rotatedCorner = this._tempVec2.copy(corner);
            mat.transformVector(rotatedCorner, rotatedCorner);
            
            const origin = new pc.Vec3().copy(this._target).add(rotatedCorner);
            const ray = new pc.Ray(origin, direction);
            
            for (const mesh of this.colliderMeshes) {
                const aabb = mesh.aabb;
                if (aabb.intersectsRay(ray, this._toWorldPoint)) {
                    const d = this._toWorldPoint.distance(origin);
                    if (d < minDistance) {
                        minDistance = d;
                        hit = true;
                    }
                }
            }
        }
        
        if (hit) {
            this._spherical.x = minDistance;
        }
    }

    private _findPointerById(pointerId: number): PointerInput | undefined {
        for (let i = 0; i < this._activePointers.length; i++) {
            if (this._activePointers[i].pointerId === pointerId) return this._activePointers[i];
        }
        return undefined;
    }

    private _findPointerByMouseButton(mouseButton: MOUSE_BUTTON): PointerInput | undefined {
        for (let i = 0; i < this._activePointers.length; i++) {
            if (this._activePointers[i].mouseButton === mouseButton) return this._activePointers[i];
        }
        return undefined;
    }

    private _disposePointer(pointer: PointerInput) {
        const index = this._activePointers.indexOf(pointer);
        if (index !== -1) this._activePointers.splice(index, 1);
    }

    private _createOnRestPromise(resolveImmediately: boolean): Promise<void> {
        if (resolveImmediately) return Promise.resolve();

        this._hasRested = false;
        this.dispatchEvent({ type: 'transitionstart' });

        return new Promise((resolve) => {
            const onResolve = () => {
                this.off('rest', onResolve);
                resolve();
            };
            this.once('rest', onResolve);
        });
    }

    private _encloseToBoundary(position: pc.Vec3, offset: pc.Vec3, friction: number): pc.Vec3 {
        const offsetLength2 = offset.lengthSq();

        if (offsetLength2 === 0.0) return position;

        const newTarget = this._tempVec.copy(offset).add(position);
        
        // Clamp point to boundary
        const clampedTarget = this._tempVec2.copy(newTarget);
        const min = this._boundary.getMin();
        const max = this._boundary.getMax();
        
        clampedTarget.x = pc.math.clamp(clampedTarget.x, min.x, max.x);
        clampedTarget.y = pc.math.clamp(clampedTarget.y, min.y, max.y);
        clampedTarget.z = pc.math.clamp(clampedTarget.z, min.z, max.z);

        const deltaClampedTarget = clampedTarget.sub(newTarget);
        const deltaClampedTargetLength2 = deltaClampedTarget.lengthSq();

        if (deltaClampedTargetLength2 === 0.0) {
            return position.add(offset);
        } else if (deltaClampedTargetLength2 === offsetLength2) {
            return position;
        } else if (friction === 0.0) {
            return position.add(offset).add(deltaClampedTarget);
        } else {
            const offsetFactor = 1.0 + friction * deltaClampedTargetLength2 / offset.dot(deltaClampedTarget);
            return position
                .add(this._tempVec.copy(offset).mulScalar(offsetFactor))
                .add(deltaClampedTarget.mulScalar(1.0 - friction));
        }
    }

    private _updateNearPlaneCorners() {
        if (!this.entity.camera) return;

        if (isPerspectiveCamera(this.entity.camera)) {
            const near = this.entity.camera.nearClip;
            const fov = this.entity.camera.fov * DEG2RAD;
            const heightHalf = Math.tan(fov * 0.5) * near;
            const widthHalf = heightHalf * this.entity.camera.aspectRatio;

            this._nearPlaneCorners[0].set(-widthHalf, -heightHalf, 0);
            this._nearPlaneCorners[1].set(widthHalf, -heightHalf, 0);
            this._nearPlaneCorners[2].set(widthHalf, heightHalf, 0);
            this._nearPlaneCorners[3].set(-widthHalf, heightHalf, 0);
        } else if (isOrthographicCamera(this.entity.camera)) {
            const height = this.entity.camera.orthoHeight;
            const width = height * this.entity.camera.aspectRatio;
            
            const left = -width / 2;
            const right = width / 2;
            const top = height / 2;
            const bottom = -height / 2;

            this._nearPlaneCorners[0].set(left, top, 0);
            this._nearPlaneCorners[1].set(right, top, 0);
            this._nearPlaneCorners[2].set(right, bottom, 0);
            this._nearPlaneCorners[3].set(left, bottom, 0);
        }
    }

    public static createBoundingSphere(entity: pc.Entity, out: pc.BoundingSphere = new pc.BoundingSphere()): pc.BoundingSphere {
        out.center.set(0, 0, 0);
        out.radius = 0;

        const box = new pc.BoundingBox();
        let first = true;

        const renders = entity.findComponents('render') as pc.RenderComponent[];
        renders.forEach(render => {
            render.meshInstances.forEach(mi => {
                if (first) {
                    box.copy(mi.aabb);
                    first = false;
                } else {
                    box.add(mi.aabb);
                }
            });
        });
        
        const models = entity.findComponents('model') as pc.ModelComponent[];
        models.forEach(model => {
             if (model.meshInstances) {
                 model.meshInstances.forEach(mi => {
                    if (first) {
                        box.copy(mi.aabb);
                        first = false;
                    } else {
                        box.add(mi.aabb);
                    }
                });
             }
        });

        if (!first) {
            out.center.copy(box.center);
            out.radius = box.halfExtents.length();
        }
        
        return out;
    }
}
